import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import api, { API_ENDPOINTS } from '../config/api';
import { stripHtmlTags } from '../utils/htmlUtils';
import { 
  Search, 
  Filter, 
  MapPin, 
  Clock, 
  DollarSign, 
  Users,
  Star,
  Eye,
  Edit,
  Trash2,
  Plus,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';

const MyFullTimeJobsClientPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    search: '',
    page: 1
  });

  // Handle URL parameters for type filtering
  useEffect(() => {
    setFilters(prev => ({ ...prev, type: 'full-time' }));
  }, []);

  // Fetch user's full-time jobs
  const { data: jobsData, isLoading, error } = useQuery(
    ['myFullTimeJobsClient', filters],
    () => api.get(API_ENDPOINTS.JOBS.MY_JOBS, { params: { ...filters, type: 'full-time' } }),
    {
      select: (data) => data.data,
      keepPreviousData: true,
    }
  );

  const jobs = jobsData?.jobs || [];
  const totalPages = jobsData?.totalPages || 1;
  const currentPage = jobsData?.currentPage || 1;

  const categories = [
    'web-development', 'mobile-development', 'design', 'writing', 'marketing',
    'data-science', 'other'
  ];

  const statuses = [
    { value: '', label: 'All Status' },
    { value: 'open', label: 'Open' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const formatBudget = (job) => {
    if (!job.budget) return 'Salary negotiable';
    if (job.budget.fixed) return `$${job.budget.fixed.toFixed(2)}/year`;
    if (job.budget.min && job.budget.max) {
      return `$${job.budget.min.toFixed(2)} - $${job.budget.max.toFixed(2)}/year`;
    }
    if (job.budget.min) return `$${job.budget.min.toFixed(2)}+/year`;
    return 'Salary negotiable';
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'in-progress': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      completed: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-heading mb-2">
              My Full-time Jobs
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage your full-time job postings and track applications
            </p>
          </div>
          <Link to="/create-job" className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Post New Job
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card p-6 mb-6">
        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search your full-time jobs..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            />
          </div>
        </div>

        {/* Filter Controls */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full input-field"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat} className="capitalize">
                  {cat.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full input-field"
            >
              {statuses.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button className="btn-outline w-full">
              <Filter className="w-4 h-4 mr-2" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Jobs Grid */}
      <div className="grid grid-cols-1 gap-6">
        {jobs.map((job) => (
          <div key={job._id} className="card p-6 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3 flex-wrap gap-2">
                  <Link 
                    to={`/jobs/${job._id}`}
                    className="text-xl font-semibold text-heading hover:text-accent transition-colors duration-200"
                  >
                    {job.title}
                  </Link>
                  <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    Full-time
                  </span>
                  {/* Application Status Counts - Show all statuses with counts */}
                  {job.applicationStatusCounts && (
                    <>
                      {job.applicationStatusCounts.completed > 0 && (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          Completed ({job.applicationStatusCounts.completed})
                        </span>
                      )}
                      {job.applicationStatusCounts.active > 0 && (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          Active ({job.applicationStatusCounts.active})
                        </span>
                      )}
                      {job.applicationStatusCounts.pending > 0 && (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                          Pending ({job.applicationStatusCounts.pending})
                        </span>
                      )}
                      {job.applicationStatusCounts.rejected > 0 && (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                          Rejected ({job.applicationStatusCounts.rejected})
                        </span>
                      )}
                    </>
                  )}
                </div>

                <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                  {stripHtmlTags(job.description)}
                </p>

                <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
                  <div className="flex items-center space-x-1">
                    <DollarSign className="w-4 h-4" />
                    <span>{formatBudget(job)}</span>
                  </div>
                  
                  {job.duration && job.duration.value && job.duration.unit && (
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{job.duration.value} {job.duration.unit}</span>
                    </div>
                  )}

                  {job.location && (
                    <div className="flex items-center space-x-1">
                      <MapPin className="w-4 h-4" />
                      <span>{job.location.remote ? 'Remote' : job.location.city || 'Location not specified'}</span>
                    </div>
                  )}

                  <div className="flex items-center space-x-1">
                    <Users className="w-4 h-4" />
                    <span>{job.applicationCount} applications</span>
                  </div>
                </div>

                {/* Applications Summary */}
                {job.applications && job.applications.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Applications Summary</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="flex items-center space-x-1">
                        <AlertCircle className="w-4 h-4 text-yellow-500" />
                        <span>{job.applications.filter(app => app.status === 'pending').length} Pending</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>{job.applications.filter(app => app.status === 'accepted').length} Accepted</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <XCircle className="w-4 h-4 text-red-500" />
                        <span>{job.applications.filter(app => app.status === 'rejected').length} Rejected</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4 text-blue-500" />
                        <span>{job.applications.filter(app => app.status === 'in-progress').length} In Progress</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Skills */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {job.skills?.slice(0, 5).map((skill, index) => (
                    <span 
                      key={index}
                      className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs rounded-md"
                    >
                      {skill}
                    </span>
                  ))}
                  {job.skills?.length > 5 && (
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 text-xs rounded-md">
                      +{job.skills.length - 5} more
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">
                      Posted {new Date(job.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/jobs/${job._id}/applications`}
                      className="btn-outline text-sm"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Applications
                    </Link>
                    {/* <Link
                      to={`/jobs/${job._id}/edit`}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:text-accent transition-colors duration-200"
                    >
                      <Edit className="w-4 h-4" />
                    </Link> */}
                    <button className="p-2 text-red-600 hover:text-red-700 transition-colors duration-200">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2 mt-8">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const page = Math.max(1, currentPage - 2) + i;
            if (page > totalPages) return null;
            
            return (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-3 py-2 text-sm rounded-lg ${
                  page === currentPage
                    ? 'bg-accent text-white'
                    : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {page}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* No Results */}
      {!isLoading && jobs.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🏢</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No full-time jobs found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            You haven't posted any full-time jobs yet. Start by creating your first job posting.
          </p>
          <Link to="/create-job" className="btn-primary">
            Post Your First Job
          </Link>
        </div>
      )}
    </div>
  );
};

export default MyFullTimeJobsClientPage;
